import { useEffect, useState } from "react";
import { Card, CardContent } from "~/components/ui/card";
import ProductListSection from "./ProductListSection";
import RecommendedSection from "./RecommendedSection";
import { dealApi, type DtoCategoryWithDeals } from "~/api/deal";
import { buildShopChannel } from "~/lib/deal-channel";
import { DealCardSkeleton } from "~/components/skeleton/DealCardSkeleton";

interface CategoryContentWithSectionsProps {
  rootCategoryId: string;
  subCategoryId?: string;
  tabId: string;
}

export default function CategoryContentWithSections({
  rootCategoryId,
  subCategoryId,
  tabId,
}: CategoryContentWithSectionsProps) {
  const [categoriesWithDeals, setCategoriesWithDeals] = useState<DtoCategoryWithDeals[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchCategoriesWithSections = async () => {
      setIsLoading(true);
      try {
        const channel = buildShopChannel(tabId);
        const response = await dealApi.getCategoryWithSections({
          entryPoint: subCategoryId || rootCategoryId,
          categoryCount: 3,
          dealCountPerCategory: 5,
          channel: JSON.stringify(channel),
        });
        setCategoriesWithDeals(response.categoriesWithDeals || []);
      } catch (error) {
        console.error("Failed to fetch categories with sections:", error);
        setCategoriesWithDeals([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategoriesWithSections();
  }, [rootCategoryId, subCategoryId, tabId]);

  // Render loading skeleton
  if (isLoading) {
    return (
      <Card className="rounded-[24px] border-none shadow-none">
        <CardContent className="space-y-[32px] px-[24px]">
          {Array.from({ length: 3 }).map((_, sectionIndex) => (
            <section key={sectionIndex}>
              <div className="mb-[16px]">
                <div className="h-[34px] w-[200px] animate-pulse rounded bg-gray-200" />
              </div>
              <div className="grid grid-cols-2 gap-[24px] md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {Array.from({ length: 5 }).map((_, index) => (
                  <DealCardSkeleton key={index} />
                ))}
              </div>
            </section>
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-[24px] border-none shadow-none">
      <CardContent className="space-y-[32px] px-[24px]">
        {/* Render sections from getCategoryWithSections */}
        {categoriesWithDeals.map(categoryWithDeals => (
          <ProductListSection
            key={categoryWithDeals.category?.categoryId}
            categoryId={categoryWithDeals.category?.categoryId || ""}
            title={categoryWithDeals.category?.category || "Unknown"}
            deals={categoryWithDeals.deals || []}
            tabId={tabId}
          />
        ))}

        {/* Recommended Section */}
        <RecommendedSection categoryId={subCategoryId || rootCategoryId} tabId={tabId} />
      </CardContent>
    </Card>
  );
}
